/**
 * Calendar access, asked of Alter's own backend rather than of Google.
 *
 * The page used to run Google's token flow itself and keep the result in a
 * closure. That kept anything long-lived from existing anywhere -- and it
 * stopped working the moment this became a home-screen app, because a
 * standalone web app on iOS has its own cookie container with no Google
 * session in it, so Google's silent renewal could never succeed and the
 * calendar demanded a full login every launch.
 *
 * Now the durable half lives in AWS. This module knows nothing about it: it
 * sends the Alter sign-in and receives an access token good for about an
 * hour. There is no refresh token here, no store, and deliberately not even
 * the words -- a page that names one is a page someone eventually tries to
 * save one in.
 *
 * One failure is named apart from the rest. "Not connected" is not a fault
 * and not a retry; it means press Connect. Everything else is one
 * `GoogleUnavailable`, because a page that distinguishes six kinds of
 * not-working gives the student six ways to read the same fact.
 */

export class GoogleNotConnected extends Error {
  constructor(message) {
    super(message || 'Google is not connected yet.');
    this.name = 'GoogleNotConnected';
    // What the screen keys off, rather than matching on a message.
    this.needsConnect = true;
  }
}

export class GoogleUnavailable extends Error {
  constructor(message) {
    super(message || 'Could not reach Google.');
    this.name = 'GoogleUnavailable';
    this.needsConnect = false;
  }
}

async function call(config, session, path, { method = 'GET', body } = {}, fetchImpl = fetch) {
  if (!config?.googleApiUrl) {
    throw new GoogleUnavailable('Google Calendar is not set up for this site yet.');
  }

  const init = { method, headers: { authorization: `Bearer ${session?.idToken}` } };
  if (body !== undefined) {
    init.headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetchImpl(`${config.googleApiUrl}${path}`, init);
  } catch {
    throw new GoogleUnavailable('No connection.');
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (response.status === 409) throw new GoogleNotConnected(payload?.message);
  if (response.status === 401 || response.status === 403) {
    throw new GoogleUnavailable('Your sign-in has expired. Sign in again.');
  }
  if (!response.ok) throw new GoogleUnavailable(payload?.message);
  return payload ?? {};
}

/** A token good for about an hour, minted from the grant already on record. */
export function googleAccessToken(config, session, fetchImpl) {
  return call(config, session, '/google/token', {}, fetchImpl);
}

/** Trade the one-time code from the consent dialog for lasting access. */
export function connectGoogle(config, session, code, fetchImpl) {
  return call(config, session, '/google/connect', { method: 'POST', body: { code } }, fetchImpl);
}

/**
 * Hand the grant back to Google and have the backend forget it.
 *
 * Never throws for "there was nothing to disconnect": pressing Disconnect
 * twice, or after the grant already lapsed, must not put a red line on screen
 * for doing something that was already true.
 */
export async function disconnectGoogle(config, session, fetchImpl) {
  try {
    return await call(config, session, '/google/disconnect', { method: 'POST' }, fetchImpl);
  } catch (error) {
    if (error instanceof GoogleNotConnected) return { revoked: false };
    throw error;
  }
}
